import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Clock, 
  Crown, 
  Lock, 
  Sparkles, 
  CreditCard,
  CheckCircle2,
  AlertTriangle,
  ArrowRight
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const TrialExpiredModal = ({ isOpen, onClose, previousTier, onContinueFree }) => {
  const navigate = useNavigate();
  
  // Ensure selectedTier is always a valid tier (pro or elite)
  const validTiers = ["pro", "elite"];
  const initialTier = validTiers.includes(previousTier) ? previousTier : "pro";
  const [selectedTier, setSelectedTier] = useState(initialTier);

  const tiers = {
    pro: {
      name: "Pro",
      price: "$29.99",
      period: "/month",
      description: "For individual barbers & stylists",
      features: [
        "Unlimited clients",
        "Appointment reminders (SMS/Email)",
        "Loyalty Rewards Program",
        "Google Calendar sync",
        "All payment methods",
        "Pro Mini-Site",
        "Advanced analytics",
        "Priority listing"
      ],
      color: "amber"
    },
    elite: {
      name: "Elite",
      price: "$49.99",
      period: "/month",
      description: "For salon & shop owners",
      features: [
        "Everything in Pro",
        "Staff Management",
        "Booth/Chair Rent Collection",
        "Payroll & Tax Tracking",
        "Multi-stylist dashboard",
        "Team Analytics",
        "Custom Branding",
        "0% Platform Fees"
      ],
      color: "purple"
    }
  };

  const handleUpgrade = () => {
    navigate(`/membership?tier=${selectedTier}&from=trial_expired`);
    onClose();
  };

  const handleContinueFree = () => {
    if (onContinueFree) {
      onContinueFree();
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-stone-900 border-stone-700 text-white p-0 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-red-500/20 p-6 border-b border-stone-700">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
              <Clock className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <DialogTitle className="text-2xl font-bold text-white">
                Your 7-Day Trial Has Ended
              </DialogTitle>
              <DialogDescription className="text-stone-400">
                To keep using the Vault and Premium features, select a plan below
              </DialogDescription>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Trial recap */}
          <div className="bg-stone-800/50 rounded-lg p-4 border border-stone-700">
            <div className="flex items-center gap-2 text-amber-400 mb-2">
              <AlertTriangle className="w-4 h-4" />
              <span className="font-medium">Premium features are now locked</span>
            </div>
            <p className="text-stone-400 text-sm">
              Your data is safe and preserved. Upgrade anytime to regain full access.
            </p>
          </div>

          {/* Tier selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(tiers).map(([key, tier]) => (
              <div
                key={key}
                onClick={() => setSelectedTier(key)}
                className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  selectedTier === key
                    ? `border-${tier.color}-500 bg-${tier.color}-500/10`
                    : "border-stone-700 hover:border-stone-600"
                }`}
              >
                {selectedTier === key && (
                  <div className={`absolute -top-2 -right-2 w-6 h-6 bg-${tier.color}-500 rounded-full flex items-center justify-center`}>
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  </div>
                )}
                
                <div className="flex items-center gap-2 mb-2">
                  <Crown className={`w-5 h-5 text-${tier.color}-400`} />
                  <span className="font-bold text-white">{tier.name}</span>
                  {key === previousTier && (
                    <Badge className="bg-stone-700 text-stone-300 text-xs">Previous</Badge>
                  )}
                </div>
                
                <div className="mb-2">
                  <span className="text-2xl font-bold text-white">{tier.price}</span>
                  <span className="text-stone-400">{tier.period}</span>
                </div>
                
                <p className="text-stone-400 text-sm mb-3">{tier.description}</p>
                
                <ul className="space-y-1">
                  {tier.features.slice(0, 4).map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-stone-300">
                      <Sparkles className={`w-3 h-3 text-${tier.color}-400`} />
                      {feature}
                    </li>
                  ))}
                  {tier.features.length > 4 && (
                    <li className="text-stone-500 text-xs">
                      + {tier.features.length - 4} more features
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>

          {/* CTA buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleUpgrade}
              className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold text-lg"
              data-testid="upgrade-now-btn"
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Upgrade to {tiers[selectedTier]?.name || "Pro"} Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            
            <Button
              onClick={handleContinueFree}
              variant="ghost"
              className="w-full text-stone-400 hover:text-white hover:bg-stone-800"
              data-testid="continue-free-btn"
            >
              Continue with limited Free version
            </Button>
          </div>

          {/* Features being lost */}
          <div className="border-t border-stone-700 pt-4">
            <p className="text-stone-500 text-xs text-center">
              💡 Your premium data (rent history, analytics, etc.) will be hidden but preserved.
              Upgrade anytime to regain access.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TrialExpiredModal;
